import { useEffect, useMemo, useState } from "react";
import { ActionList } from "../../components/ui";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import SortIcon from "../../components/ui/Sorting";
import SortSummary from "../../components/ui/SortSummary";
import { formatDateTime } from "../../helpers/formatDateTime";
import type { CshGroup } from "../../services/csh/csh.types";
import { CshGroupDetailsPanel } from "./CshGroupDetailsPanel";

const COLUMNS = [
    { key: "name", label: "CSH Name" },
    { key: "createdAt", label: "Created" },
    { key: "updatedAt", label: "Updated" },
] as const;
type SortColumn = typeof COLUMNS[number]["key"];

interface Props {
    groups: CshGroup[];
    loading: boolean;
    canManage: boolean;
    downloadingSites: boolean;
    downloadingUsers: boolean;
    onDownload: (id: string, kind: "sites" | "users") => void;
    onEdit: (group: CshGroup) => void;
    onDelete: (group: CshGroup) => void;
}

export function CshGroupList({ groups, loading, canManage, downloadingSites, downloadingUsers, onDownload, onEdit, onDelete }: Props) {
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const activeColumn = COLUMNS.find(column => column.key === sortColumn);
    const sortedGroups = useMemo(() => {
        if (!sortColumn) return groups;
        const value = (group: CshGroup) => sortColumn === "name"
            ? group.name.toLowerCase() : new Date(group[sortColumn]).getTime();
        return [...groups].sort((left, right) => {
            const first = value(left);
            const second = value(right);
            return (first < second ? -1 : first > second ? 1 : 0) * (sortOrder === "asc" ? 1 : -1);
        });
    }, [groups, sortColumn, sortOrder]);
    useEffect(() => {
        if (!sortedGroups.some(group => group.id === selectedId)) setSelectedId(sortedGroups[0]?.id ?? null);
    }, [sortedGroups, selectedId]);
    const selectedGroup = groups.find(group => group.id === selectedId);
    const sort = (column: SortColumn) => {
        setSortColumn(column);
        setSortOrder(previous => sortColumn === column && previous === "asc" ? "desc" : "asc");
    };
    const reset = () => { setSortColumn(null); setSortOrder("desc"); };

    return (
        <>
            <div className="alarm-content--heading">
                <div>
                    <h3 className="heading heading__title--csh">CSH Group List</h3>
                    {activeColumn && <SortSummary columnLabel={activeColumn.label} sortOrder={sortOrder} onReset={reset} />}
                </div>
            </div>
            {loading && <LoadingSpinner message="Loading CSH Groups…" />}
            {!loading && (
                <div className="admin-requests">
                    <div className="admin-requests__table">
                        <table className="table">
                            <thead><tr>
                                {COLUMNS.map(column => (
                                    <th key={column.key} onClick={() => sort(column.key)} className="th--sortable"
                                        aria-sort={sortColumn === column.key ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}>
                                        {column.label}<SortIcon sortOrder={sortColumn === column.key ? sortOrder : null} />
                                    </th>
                                ))}
                                {canManage && <th>Actions</th>}
                            </tr></thead>
                            <tbody>
                                {!sortedGroups.length ? (
                                    <tr><td colSpan={canManage ? 4 : 3} style={{ textAlign: "center" }}>No groups have been assigned yet.</td></tr>
                                ) : sortedGroups.map(group => (
                                    <tr key={group.id} onClick={() => setSelectedId(group.id)}
                                        className={`admin-request-row${selectedId === group.id ? " admin-request-row--selected" : ""}`}>
                                        <td>{group.name}</td>
                                        <td>{formatDateTime(group.createdAt)}</td>
                                        <td>{formatDateTime(group.updatedAt)}</td>
                                        {canManage && (
                                            <td><div onClick={event => event.stopPropagation()} onKeyDown={event => {
                                                if (event.key === "Enter" || event.key === " ") event.stopPropagation();
                                            }}>
                                                <ActionList onEdit={() => onEdit(group)} onDelete={() => onDelete(group)} />
                                            </div></td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="admin-requests__panel mb-1">
                        {selectedGroup ? (
                            <CshGroupDetailsPanel group={selectedGroup} canManage={canManage}
                                downloadingSites={downloadingSites} downloadingUsers={downloadingUsers}
                                onDownload={onDownload} onEdit={onEdit} onDelete={onDelete} />
                        ) : <div>Select a group to view details.</div>}
                    </div>
                </div>
            )}
        </>
    );
}
